import React from 'react';
import { twMerge } from 'tailwind-merge';
import OrgNode from './OrgNode';
import { groupChildrenByDepartment } from './orgTree';
import { colorsForDepartment } from './departmentColors';

const EMPTY_SET = new Set();

// The department caption chip rendered above each branch group - reuses the
// same deterministic department->colour mapping as the card's own pill
// (`departmentColors.js`), so a branch's label and the cards hanging off it
// always agree on colour. A null-department group gets the same neutral
// grey treatment `OrgNode` gives a missing department, not a "colour 0"
// pill from the mapping.
const DeptGroupLabel = ({ parentId, group }) => {
  const colors = group.department_id != null ? colorsForDepartment(group.department_id) : null;
  return (
    <div
      data-testid={`org-branch-dept-${parentId}-${group.key}`}
      className="mb-2 flex justify-center"
    >
      <span
        className={twMerge(
          'inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide',
          colors ? colors.pill : 'bg-gray-100 text-gray-500'
        )}
      >
        {group.department_name}
      </span>
    </div>
  );
};

/**
 * OrgChartTree - renders a forest of org-chart nodes as nested `<ul>/<li>`
 * elements. `orgchart.css` draws the connectors via CSS pseudo-elements, so
 * this component only owns the DOM shape, not the lines themselves.
 *
 * In scoped mode every ancestor above the caller has exactly one child (the
 * server only ever sends the spine, never a sibling manager's people), so the
 * very same recursive renderer produces the single-column "spine" look the
 * design calls for - no separate scoped-mode code path is needed. The
 * `org-tree__branch--single` class just tightens the connector spacing for
 * that shape.
 *
 * The "No reporting chain" orphans panel used to render here too, inline to
 * the right of the tree inside this same scrollable row - moved out to
 * `OrgOrphansPanel.js` (round 5, ux-fixes-brief.md Task 3): rendered as a
 * toggleable overlay in the canvas shell instead, so it can never be clipped
 * by the tree's own horizontal scroll. See that component's doc comment.
 */
// Same "has a department" predicate OrgNode's own card pill and
// groupChildrenByDepartment already use - a node with an empty/missing
// department_name reads as "No department" here too, so a group's normalized
// name can be compared directly against a node's own normalized name.
const normalizedDeptName = (name) => name || 'No department';

const OrgChartTree = ({
  roots,
  mode,
  me,
  collapsedIds = EMPTY_SET,
  onToggle,
  highlightedIds = EMPTY_SET,
  dimmedIds = EMPTY_SET,
}) => {
  // `isRoot` marks a forest root (depth 0, no parent of its own) - its own
  // groups always show their label regardless of the root's own department,
  // since there is no "parent department" to compare against (a root can
  // never be "continuing" a branch). Every other node compares each child
  // group's department to ITS OWN department: a group whose department
  // matches its parent's is a same-department continuation (not a new
  // function) and its label is suppressed - only a real department change
  // gets a label. See ux-fixes-brief.md Task 1.
  const renderNode = (node, isRoot = false) => {
    const isCollapsed = collapsedIds.has(node.id);
    const hasChildren = node.children && node.children.length > 0;
    const isMe = me != null && node.id === me;
    const showNoReports = mode === 'scoped' && isMe && !hasChildren;
    const nodeDeptName = normalizedDeptName(node.department_name);

    return (
      <li
        key={node.id}
        className={node.children && node.children.length === 1 ? 'org-tree__branch--single' : undefined}
      >
        <OrgNode
          node={node}
          isMe={isMe}
          isCollapsed={isCollapsed}
          onToggle={onToggle}
          highlighted={highlightedIds.has(node.id)}
          dimmed={dimmedIds.has(node.id)}
        />
        {hasChildren && !isCollapsed && (
          // One group per department, each its own bracket: an <li> per
          // group (not a plain <div>) so it participates in the SAME
          // hand-rolled ul/li connector CSS as an ordinary child row
          // (orgchart.css's `.org-tree li`/`ul ul::before` rules key off
          // element type, not a class) - a lone group already collapses to
          // "no bracket" via the existing `:only-child` rules, and multiple
          // groups get a real sibling bracket fanning out from the parent,
          // for free. See the connector comment block at the top of
          // orgchart.css before touching this shape.
          <ul className="org-tree__groups">
            {groupChildrenByDepartment(node.children).map((group) => {
              const showLabel = isRoot || group.department_name !== nodeDeptName;
              return (
                <li key={group.key} className="org-tree__group">
                  {showLabel && <DeptGroupLabel parentId={node.id} group={group} />}
                  <ul>{group.children.map((child) => renderNode(child, false))}</ul>
                </li>
              );
            })}
          </ul>
        )}
        {showNoReports && (
          <p data-testid="org-no-reports" className="org-tree__no-reports">
            No direct reports
          </p>
        )}
      </li>
    );
  };

  return (
    <div className="org-chart-forest-row">
      <div className="org-tree">
        <ul>{(roots || []).map((root) => renderNode(root, true))}</ul>
      </div>
    </div>
  );
};

export default OrgChartTree;
